import { ref, Ref, watch } from 'vue';
import { useActiveElement } from '@vueuse/core';
import { mustDeferTier, TCatalogTier } from '@/domain/catalog/catalog-layout';

/** The tiers the focused element's `data-tier-only` subtree exists in; null when none. */
function focusedScopes(): string[] | null {
  const scope = document.activeElement?.closest?.('[data-tier-only]');
  const value = scope?.getAttribute('data-tier-only');
  return value ? value.split(/\s+/) : null;
}

/**
 * The page tier, applied only when no focused field would be unmounted by
 * it (AGENTS.md adaptive layout rule): a field inside a `data-tier-only`
 * subtree that the next tier drops keeps the current tier until focus
 * leaves it (the `getTierTransitionUnmounts` pattern of the POS).
 */
export function useFocusSafeTier(target: Ref<TCatalogTier>) {
  const tier = ref<TCatalogTier>(target.value);
  const activeElement = useActiveElement();

  function evaluate() {
    if (target.value === tier.value) return;
    if (mustDeferTier(focusedScopes(), target.value)) return;
    tier.value = target.value;
  }

  watch(target, evaluate);
  watch(activeElement, evaluate);

  return { tier };
}
